import React, { useState, useEffect } from "react";
import { useQuery } from "wasp/client/operations";
import { getAllUsers, copyCollectionToAdmin, deleteAdminCollection, getEnrichmentJobsForCollection } from "wasp/client/operations";
import { getAllCollectionsOfUserForSpyview } from "wasp/client/operations";
import DefaultLayout from "../layout/DefaultLayout";
import { useAuth } from "wasp/client/auth";
import { Loader2, Trash2, Copy, ChevronDown, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Search, X, Info } from "lucide-react";
import { useDispatch } from "react-redux";
import { setCurrentCollection } from "../../store/userSlice";

interface Collection {
  id: string;
  name: string;
  createdAt: string;
  tableId?: string;
  userId: string;
  isDummy?: boolean;
  enrichmentJobs?: any[];
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface UsersResponse {
  users: any[];
  pagination: Pagination;
}

// Search Input Component
const SearchInput = ({ 
  value, 
  onChange, 
  onClear,
  placeholder 
}: { 
  value: string; 
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  placeholder: string;
}) => {
  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
        <Search className="w-4 h-4 text-gray-500" />
      </div>
      <input
        type="text"
        value={value}
        onChange={onChange}
        className="block w-full p-2 pl-10 pr-10 text-sm border border-gray-300 rounded-lg bg-white focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-purple-500 dark:focus:border-purple-500"
        placeholder={placeholder}
      />
      {value && (
        <button
          type="button"
          onClick={onClear}
          className="absolute inset-y-0 right-0 flex items-center pr-3"
        >
          <X className="w-4 h-4 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" />
        </button>
      )}
    </div>
  );
};

// Pagination Component
const PaginationControls = ({ 
  currentPage, 
  totalPages, 
  onPageChange,
  totalItems,
  itemsPerPage,
  currentItems
}: { 
  currentPage: number; 
  totalPages: number; 
  onPageChange: (page: number) => void;
  totalItems: number;
  itemsPerPage: number;
  currentItems: number;
}) => {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    
    // Always show first page
    if (currentPage > 3) {
      pages.push(1);
      // Add ellipsis if there's a gap
      if (currentPage > 4) {
        pages.push('...');
      }
    }
    
    // Show pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(i);
    }
    
    // Always show last page
    if (currentPage < totalPages - 2) {
      // Add ellipsis if there's a gap
      if (currentPage < totalPages - 3) {
        pages.push('...');
      }
      pages.push(totalPages);
    }
    
    return pages;
  };
  
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pb-4">
      <div className="text-sm text-gray-500">
        Showing <span className="font-medium">{currentItems}</span> of <span className="font-medium">{totalItems}</span> users
      </div>
      
      <div className="flex items-center">
        <div className="flex items-center space-x-1">
          {/* First Page */}
          <button 
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            aria-label="Go to first page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
          
          {/* Previous Page */}
          <button 
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            aria-label="Go to previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          
          {/* Page Numbers */}
          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
                ...
              </span>
            ) : (
              <button
                key={`page-${page}`}
                onClick={() => onPageChange(page as number)}
                className={`px-3 py-1 rounded-md ${
                  currentPage === page
                    ? 'bg-purple-500 text-white'
                    : 'text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            )
          ))}
          
          {/* Next Page */}
          <button 
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            aria-label="Go to next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          
          {/* Last Page */}
          <button 
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            aria-label="Go to last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminUsersCollectionsPage = () => {
  const { data: currentUser } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchCollectionId, setSearchCollectionId] = useState('');
  const [debouncedSearchEmail, setDebouncedSearchEmail] = useState('');
  const [debouncedSearchCollectionId, setDebouncedSearchCollectionId] = useState('');
  
  // Zenginleştirme işleri için state
  const [selectedCollectionForJobs, setSelectedCollectionForJobs] = useState<string | null>(null);
  const [showJobsModal, setShowJobsModal] = useState(false);
  
  // Zenginleştirme işlerini getir
  const { data: enrichmentJobs, isLoading: isLoadingJobs, refetch: refetchJobs } = useQuery(
    getEnrichmentJobsForCollection, 
    selectedCollectionForJobs ? { collectionId: selectedCollectionForJobs } : null
  );
  
  // Ensure enrichmentJobsArray is always an array
  const enrichmentJobsArray = Array.isArray(enrichmentJobs) ? enrichmentJobs : [];
  
  // Debounce search inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchEmail(searchEmail);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchEmail]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchCollectionId(searchCollectionId);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchCollectionId]);
  
  const { data: usersResponse, isLoading, error, refetch: refetchUsers } = useQuery(getAllUsers, {
    page: currentPage,
    limit: usersPerPage,
    searchEmail: debouncedSearchEmail,
    searchCollectionId: debouncedSearchCollectionId
  });
  
  const { data, isLoading: isLoadingAdminCollections, error: adminCollectionsError, refetch: refetchAdminCollections } = useQuery(getAllCollectionsOfUserForSpyview);
  
  // Ensure adminCollections is always an array, even if data is null or undefined
  const adminCollections: Collection[] = Array.isArray(data) ? data : [];
  const users = usersResponse?.users || [];
  const pagination = usersResponse?.pagination || { total: 0, page: 1, limit: usersPerPage, totalPages: 1 };

  const [selectedCollection, setSelectedCollection] = useState<{ id: string, name: string } | null>(null);
  const [copying, setCopying] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<{ id: string, name: string, enrichmentJobsCount: number } | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<string[]>([]);
  
  const dispatch = useDispatch();

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchEmail(e.target.value);
  };
  
  const handleSearchCollectionIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCollectionId(e.target.value);
  };
  
  const clearSearchEmail = () => {
    setSearchEmail('');
  };
  
  const clearSearchCollectionId = () => {
    setSearchCollectionId('');
  };

  // Refetch users when page or search changes
  useEffect(() => {
    refetchUsers();
  }, [currentPage, debouncedSearchEmail, debouncedSearchCollectionId]);

  if (!currentUser) {
    return null;
  }

  if (isLoading) {
    return (
      <DefaultLayout user={currentUser}>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DefaultLayout>
    );
  }

  if (error) {
    return (
      <DefaultLayout user={currentUser}>
        <div className="p-4 bg-red-100 text-red-800 rounded-md">
          Error: {error.message}
        </div>
      </DefaultLayout>
    );
  }

  const toggleUserExpanded = (userId: string) => {
    setExpandedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const handleCopyCollection = async (userId: string, collectionId: string, collectionName: string) => {
    try {
      setCopying(true);
      setSelectedCollection({ id: collectionId, name: collectionName });
      
      const result = await copyCollectionToAdmin({ 
        collectionId: collectionId,
        userId: userId
      });
      
      if (result.success) {
        setCopySuccess({ 
          id: result.copiedCollection.id, 
          name: result.copiedCollection.name,
          enrichmentJobsCount: result.jobsCopied || 0
        });
        
        // Check if copiedCollection exists before dispatching
        if (result.copiedCollection && typeof result.copiedCollection === 'object') {
          dispatch(setCurrentCollection(result.copiedCollection));
        } else {
          console.error("Failed to set current collection: copiedCollection is missing or invalid");
        }
        
        refetchAdminCollections();
        
        setTimeout(() => {
          window.location.href = `/admin/products`;
        }, 1000);
      } else {
        throw new Error("Failed to copy collection");
      }
    } catch (err) {
      console.error("Failed to copy collection:", err);
      setCopying(false);
      setSelectedCollection(null);
    }
  };

  const handleDeleteCollection = async (collectionId: string, collectionName: string) => {
    try {
      if (!confirm(`Are you sure you want to delete this collection: "${collectionName}"?`)) {
        return;
      }
      
      setDeleting(collectionId);
      
      const result = await deleteAdminCollection({ 
        collectionId: collectionId 
      });
      
      if (result.success) {
        setDeleteSuccess(result.message);
        
        refetchAdminCollections();
        
        setTimeout(() => {
          setDeleteSuccess(null);
        }, 3000);
      } else {
        throw new Error("Failed to delete collection");
      }
    } catch (err) {
      console.error("Failed to delete collection:", err);
      setDeleting(null);
    }
  };

  const handleViewEnrichmentJobs = (collectionId: string) => {
    if (!collectionId) {
      console.error("Cannot view enrichment jobs: Collection ID is missing");
      return;
    }
    setSelectedCollectionForJobs(collectionId);
    setShowJobsModal(true);
    refetchJobs();
  };

  return (
    <DefaultLayout user={currentUser}>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Spyview</h1>
          
          <div className="text-sm text-gray-500">
            Total Users: <span className="font-medium">{pagination.total}</span>
          </div>
        </div>
        
        {copySuccess && (
          <div className="p-4 bg-green-100 text-green-800 rounded-md">
            Collection successfully copied: {copySuccess.name}. 
            {copySuccess.enrichmentJobsCount > 0 && ` ${copySuccess.enrichmentJobsCount} enrichment jobs were also copied.`} 
            Redirecting to Products page...
          </div>
        )}
        
        {deleteSuccess && (
          <div className="p-4 bg-green-100 text-green-800 rounded-md">
            {deleteSuccess}
          </div>
        )}
        
        {adminCollectionsError && (
          <div className="p-4 bg-red-100 text-red-800 rounded-md">
            Error loading copied collections: {adminCollectionsError.message}
          </div>
        )}
        
        {/* Kopyalanmış Koleksiyonlar - Şimdi üstte */}
        <div>
          <h2 className="text-xl font-bold mb-4">Copied Collections</h2>
          
          {isLoadingAdminCollections ? (
            <div className="flex items-center justify-center h-24">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : Array.isArray(adminCollections) && adminCollections.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {adminCollections.map((collection: any) => (
                <div key={collection.id} className="border rounded-lg p-4 bg-white dark:bg-gray-800">
                  <div className="flex flex-col h-full">
                    <div>
                      <h3 className="font-medium">{collection.name}</h3>
                      <p className="text-sm text-gray-500">Created: {new Date(collection.createdAt).toLocaleString()}</p>
                      <p className="text-xs font-mono text-gray-500 mt-1">ID: {collection.id}</p>
                      {collection.tableId && (
                        <p className="text-xs font-mono text-gray-500">Table ID: {collection.tableId}</p>
                      )}
                      {collection.enrichmentJobs && collection.enrichmentJobs.length > 0 && (
                        <p className="text-xs text-blue-500 mt-1">
                          <span className="font-semibold">{collection.enrichmentJobs.length}</span> enrichment jobs
                        </p>
                      )}
                    </div>
                    
                    <div className="flex space-x-2 mt-4 pt-4 border-t">
                      <button 
                        className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors flex items-center"
                        disabled={deleting === collection.id}
                        onClick={() => handleDeleteCollection(collection.id, collection.name)}
                      >
                        {deleting === collection.id ? 
                          <>
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Deleting...
                          </> : 
                          <>
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </>
                        }
                      </button>
                      
                      {collection.enrichmentJobs && collection.enrichmentJobs.length > 0 && (
                        <button 
                          className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors flex items-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewEnrichmentJobs(collection.id);
                          }}
                        >
                          <Info className="h-3 w-3 mr-1" />
                          View Jobs
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
              <p className="text-gray-500">You don't have any copied collections yet.</p>
              <p className="text-sm text-gray-400 mt-2">You can copy a collection from the list below to your account.</p>
            </div>
          )}
        </div>
        
        {/* Search Filters */}
        <div>
          <h2 className="text-xl font-bold mb-4">All Users and Collections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <SearchInput 
              value={searchEmail} 
              onChange={handleSearchEmailChange} 
              onClear={clearSearchEmail}
              placeholder="Search by user email..."
            />
            
            <SearchInput 
              value={searchCollectionId} 
              onChange={handleSearchCollectionIdChange} 
              onClear={clearSearchCollectionId}
              placeholder="Search by collection ID..."
            />
          </div>
        </div>
        
        <div className="space-y-4">
          {users && users.length > 0 ? (
            users.map((user: any) => (
              <div key={user.id} className="border rounded-lg overflow-hidden">
                <div 
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 cursor-pointer"
                  onClick={() => toggleUserExpanded(user.id)}
                >
                  <div className="flex items-center space-x-4">
                    {expandedUsers.includes(user.id) ? 
                      <ChevronDown className="h-4 w-4" /> : 
                      <ChevronRight className="h-4 w-4" />
                    }
                    <div>
                      <div className="font-medium">{user.email}</div>
                      <div className="text-sm text-gray-500">
                        {user.isAdmin ? "Admin" : "User"} • 
                        Collections: {user.miqCollections.length} • 
                        Created: {new Date(user.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs font-mono text-gray-500">{user.id}</div>
                </div>
                
                {expandedUsers.includes(user.id) && (
                  <div className="p-4 bg-white dark:bg-gray-900">
                    {user.miqCollections.length > 0 ? (
                      <div className="space-y-4">
                        {user.miqCollections.map((collection: any) => (
                          <div 
                            key={collection.id} 
                            className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border"
                          >
                            <div className="flex justify-between">
                              <div>
                                <h3 className="font-medium">{collection.name}</h3>
                                <p className="text-sm text-gray-500">Created: {new Date(collection.createdAt).toLocaleString()}</p>
                                <p className="text-xs font-mono text-gray-500 mt-1">ID: {collection.id}</p>
                                {collection.tableId && (
                                  <p className="text-xs font-mono text-gray-500">Table ID: {collection.tableId}</p>
                                )}
                                {collection.enrichmentJobs && collection.enrichmentJobs.length > 0 && (
                                  <p className="text-xs text-blue-500 mt-1">
                                    <span className="font-semibold">{collection.enrichmentJobs.length}</span> enrichment jobs
                                  </p>
                                )}
                              </div>
                              <div className="flex space-x-2">
                                <button 
                                  className="px-3 py-1 bg-purple-500 text-white text-sm rounded hover:bg-purple-600 transition-colors flex items-center"
                                  disabled={copying}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyCollection(user.id, collection.id, collection.name);
                                  }}
                                >
                                  {copying && selectedCollection?.id === collection.id ? 
                                    <>
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                      Copying...
                                    </> : 
                                    <>
                                      <Copy className="h-3 w-3 mr-1" />
                                      Copy and Use
                                    </>
                                  }
                                </button>
                                
                                {collection.enrichmentJobs && collection.enrichmentJobs.length > 0 && (
                                  <button 
                                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors flex items-center"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewEnrichmentJobs(collection.id);
                                    }}
                                  >
                                    <Info className="h-3 w-3 mr-1" />
                                    View Jobs
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-500 italic">This user has no collections.</div>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="p-8 text-center bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-500">No users found matching your search criteria.</p>
              {(searchEmail || searchCollectionId) && (
                <button 
                  onClick={() => {
                    clearSearchEmail();
                    clearSearchCollectionId();
                  }}
                  className="mt-2 text-purple-500 hover:text-purple-700"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
          
          {/* Pagination Controls */}
          {pagination && pagination.totalPages > 1 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit}
              currentItems={users.length}
            />
          )}
        </div>
        
        {/* Zenginleştirme İşleri Modal */}
        {showJobsModal && selectedCollectionForJobs && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Enrichment Jobs</h2>
                <button 
                  onClick={() => {
                    setShowJobsModal(false);
                    setSelectedCollectionForJobs(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {isLoadingJobs ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : enrichmentJobsArray && enrichmentJobsArray.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 mb-4">
                    Total of {enrichmentJobsArray.length} enrichment jobs found.
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Started</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ended</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Progress</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Product Count</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {enrichmentJobsArray.map((job: any) => (
                          <tr key={job.id || `job-${Math.random()}`}>
                            <td className="px-6 py-4 whitespace-nowrap text-xs font-mono">{job.id || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                job.status === 'completed' ? 'bg-green-100 text-green-800' :
                                job.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                job.status === 'failed' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {job.status || 'unknown'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {job.startedAt ? new Date(job.startedAt).toLocaleString() : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {job.endedAt ? new Date(job.endedAt).toLocaleString() : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {job.progress !== null && job.progress !== undefined ? `${job.progress}%` : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {job.productCount || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-10">
                  <p className="text-gray-500">No enrichment jobs found for this collection.</p>
                </div>
              )}
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setShowJobsModal(false);
                    setSelectedCollectionForJobs(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DefaultLayout>
  );
};

export default AdminUsersCollectionsPage; 